/**
 * Rate Limiting Hook
 *
 * 클라이언트 측 요청 제한을 위한 훅입니다.
 * DDoS 공격 방지 및 서버 보호를 위해 사용합니다.
 *
 * 작동 방식:
 * 1. localStorage에 요청 기록 저장 (타임스탬프 배열)
 * 2. 일정 시간 내 요청 횟수 체크
 * 3. 제한 초과 시 남은 대기 시간 반환
 *
 * 주의: 클라이언트 측 제한은 쉽게 우회 가능합니다.
 * 반드시 백엔드에서도 slowapi 등으로 제한해야 합니다!
 * 이 훅은 일반 사용자의 실수 방지 및 UX 개선 목적입니다.
 */

import { useState, useEffect, useCallback } from 'react'

// =============================================================================
// 타입 정의
// =============================================================================

/** Rate Limit 설정 */
interface RateLimitConfig {
  /** 제한할 요청 종류 식별자 (예: 'humming', 'lyrics') */
  key: string
  /** 시간 윈도우 (밀리초) - 이 시간 동안의 요청을 카운트 */
  windowMs: number
  /** 최대 요청 횟수 - windowMs 동안 이만큼까지 허용 */
  maxRequests: number
}

/** Rate Limit 상태 */
interface RateLimitState {
  /** 요청 가능 여부 */
  canRequest: boolean
  /** 남은 요청 횟수 */
  remainingRequests: number
  /** 제한 해제까지 남은 시간 (초) */
  secondsUntilReset: number
  /** 요청 기록 함수 - API 호출 전에 실행 */
  recordRequest: () => boolean
  /** 상태 초기화 함수 */
  reset: () => void
}

// =============================================================================
// 상수
// =============================================================================

/** localStorage 키 prefix */
const STORAGE_PREFIX = 'rateLimit_'

/** 기본 설정값 */
const DEFAULT_CONFIG: Omit<RateLimitConfig, 'key'> = {
  windowMs: 60 * 1000,  // 1분
  maxRequests: 5,       // 분당 5회
}

// =============================================================================
// 유틸리티 함수
// =============================================================================

/**
 * localStorage에서 요청 기록 가져오기
 *
 * @param key - 요청 종류 식별자
 * @returns 타임스탬프 배열 (밀리초)
 */
function getRequestHistory(key: string): number[] {
  try {
    const stored = localStorage.getItem(STORAGE_PREFIX + key)
    if (stored) {
      return JSON.parse(stored)
    }
  } catch (e) {
    // localStorage 접근 실패 또는 파싱 실패
    console.warn('Failed to read rate limit history:', e)
  }
  return []
}

/**
 * localStorage에 요청 기록 저장
 *
 * @param key - 요청 종류 식별자
 * @param history - 타임스탬프 배열
 */
function saveRequestHistory(key: string, history: number[]): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(history))
  } catch (e) {
    // localStorage 저장 실패 (용량 초과 등)
    console.warn('Failed to save rate limit history:', e)
  }
}

/**
 * 유효한 요청 기록만 필터링
 *
 * windowMs 이내의 요청만 남기고 오래된 것은 제거
 *
 * @param history - 전체 요청 기록
 * @param windowMs - 시간 윈도우
 * @returns 유효한 요청 기록
 */
function filterValidHistory(history: number[], windowMs: number): number[] {
  const now = Date.now()
  const windowStart = now - windowMs

  // windowStart 이후의 요청만 유지
  return history.filter(timestamp => timestamp > windowStart)
}

// =============================================================================
// Hook
// =============================================================================

/**
 * useRateLimit Hook
 *
 * 클라이언트 측 요청 제한을 관리합니다.
 *
 * @param config - Rate Limit 설정
 * @returns Rate Limit 상태 및 함수들
 *
 * @example
 * ```tsx
 * function SearchButton() {
 *   const {
 *     canRequest,
 *     remainingRequests,
 *     secondsUntilReset,
 *     recordRequest
 *   } = useRateLimit({
 *     key: 'humming',
 *     windowMs: 60000,  // 1분
 *     maxRequests: 5    // 분당 5회
 *   })
 *
 *   const handleSearch = async () => {
 *     // 1. 요청 가능한지 확인 & 기록
 *     if (!recordRequest()) {
 *       alert(`${secondsUntilReset}초 후에 다시 시도해주세요`)
 *       return
 *     }
 *
 *     // 2. 실제 API 호출
 *     await api.search(...)
 *   }
 *
 *   return (
 *     <button onClick={handleSearch} disabled={!canRequest}>
 *       검색 ({remainingRequests}회 남음)
 *     </button>
 *   )
 * }
 * ```
 */
export function useRateLimit(config: Partial<RateLimitConfig> & { key: string }): RateLimitState {
  // 설정 병합
  const { key, windowMs, maxRequests } = {
    ...DEFAULT_CONFIG,
    ...config
  }

  // ---------------------------------------------------------------------------
  // 상태
  // ---------------------------------------------------------------------------

  const [history, setHistory] = useState<number[]>(() =>
    filterValidHistory(getRequestHistory(key), windowMs)
  )

  // ---------------------------------------------------------------------------
  // 계산된 값
  // ---------------------------------------------------------------------------

  /** 유효한 요청 기록 (현재 윈도우 내) */
  const validHistory = filterValidHistory(history, windowMs)

  /** 요청 가능 여부 */
  const canRequest = validHistory.length < maxRequests

  /** 남은 요청 횟수 */
  const remainingRequests = Math.max(0, maxRequests - validHistory.length)

  /** 가장 오래된 유효 요청의 타임스탬프 */
  const oldestRequest = validHistory.length > 0 ? Math.min(...validHistory) : 0

  /** 제한 해제까지 남은 시간 (초) */
  const secondsUntilReset = canRequest
    ? 0
    : Math.ceil((oldestRequest + windowMs - Date.now()) / 1000)

  // ---------------------------------------------------------------------------
  // 요청 기록 함수
  // ---------------------------------------------------------------------------

  /**
   * 요청 기록
   *
   * API 호출 전에 반드시 호출해야 합니다.
   * 요청 가능하면 기록하고 true 반환, 불가능하면 false 반환
   *
   * @returns 요청 성공 여부
   */
  const recordRequest = useCallback((): boolean => {
    // 현재 유효한 기록 필터링
    const currentValid = filterValidHistory(getRequestHistory(key), windowMs)

    // 제한 체크
    if (currentValid.length >= maxRequests) {
      return false
    }

    // 새 요청 기록 추가
    const newHistory = [...currentValid, Date.now()]

    // 상태 & localStorage 업데이트
    setHistory(newHistory)
    saveRequestHistory(key, newHistory)

    return true
  }, [key, windowMs, maxRequests])

  /**
   * 상태 초기화
   *
   * 모든 요청 기록을 삭제합니다.
   * 테스트 또는 관리 목적으로 사용
   */
  const reset = useCallback(() => {
    setHistory([])
    localStorage.removeItem(STORAGE_PREFIX + key)
  }, [key])

  // ---------------------------------------------------------------------------
  // 자동 갱신
  // ---------------------------------------------------------------------------

  /**
   * 1초마다 상태 갱신
   *
   * 오래된 요청이 만료되면 자동으로 canRequest가 true로 변경됨
   */
  useEffect(() => {
    // 제한 상태가 아니면 타이머 불필요
    if (canRequest) return

    const timer = setInterval(() => {
      const updated = filterValidHistory(getRequestHistory(key), windowMs)
      setHistory(updated)
      saveRequestHistory(key, updated)
    }, 1000)

    return () => clearInterval(timer)
  }, [canRequest, key, windowMs])

  // ---------------------------------------------------------------------------
  // 반환
  // ---------------------------------------------------------------------------

  return {
    canRequest,
    remainingRequests,
    secondsUntilReset,
    recordRequest,
    reset
  }
}

// =============================================================================
// 미리 정의된 Rate Limit 설정
// =============================================================================

/**
 * 허밍 검색용 Rate Limit 설정
 *
 * 허밍 검색은 서버 부하가 크므로 더 엄격하게 제한
 * - 1분에 3회
 * - 오디오 처리 + 벡터 검색이 무거움
 */
export const HUMMING_RATE_LIMIT: RateLimitConfig = {
  key: 'humming_search',
  windowMs: 60 * 1000,   // 1분
  maxRequests: 3,        // 분당 3회
}

/**
 * 가사 검색용 Rate Limit 설정
 *
 * 텍스트 검색은 상대적으로 가벼움
 * - 1분에 10회
 */
export const LYRICS_RATE_LIMIT: RateLimitConfig = {
  key: 'lyrics_search',
  windowMs: 60 * 1000,   // 1분
  maxRequests: 10,       // 분당 10회
}
